import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  classifyDomain,
  bulkClassifyDomains,
  removeOrgIgnoreDomain,
  removeClassifiedDomain,
} from '@kirin/api-client/api/shadow-ai';
import type { PendingDomainClassification } from '@kirin/api-client/contracts/shadow-ai';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';

export function useClassifyDomain(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domain,
      classification,
      options,
    }: {
      domain: string;
      classification: PendingDomainClassification;
      options?: { category?: string; name?: string; notes?: string };
    }) =>
      classifyDomain(
        apiClient,
        domain,
        classification,
        organizationId,
        options
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.networkDiscovery.all,
      });
    },
  });
}

export function useBulkClassify(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domains,
      classification,
      options,
    }: {
      domains: string[];
      classification: PendingDomainClassification;
      options?: { category?: string; notes?: string; name?: string };
    }) =>
      bulkClassifyDomains(
        apiClient,
        domains,
        classification,
        organizationId,
        options
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.networkDiscovery.all,
      });
    },
  });
}

export function useRemoveOrgIgnoreDomain(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domain: string) =>
      removeOrgIgnoreDomain(apiClient, organizationId, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.networkDiscovery.orgIgnore(organizationId),
      });
    },
  });
}

export function useRemoveClassifiedDomain(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domain: string) =>
      removeClassifiedDomain(apiClient, domain, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.networkDiscovery.all,
      });
    },
  });
}
